import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Download, File, Image as ImageIcon, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useDocuments } from '@/hooks/useDocuments';
import { format } from 'date-fns';

interface SpecificationDocumentCardProps {
  documentId: string;
  specificationName?: string;
  subcategoryName?: string;
  productCategoryName?: string;
  showDownloadButton?: boolean;
  showMetadata?: boolean;
  compact?: boolean;
}

export const SpecificationDocumentCard = ({
  documentId,
  specificationName,
  subcategoryName,
  productCategoryName,
  showDownloadButton = true,
  showMetadata = true,
  compact = false
}: SpecificationDocumentCardProps) => {
  const [document, setDocument] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const { getDownloadUrl } = useDocuments({ entityType: 'specification', entityId: documentId });

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const { data, error } = await supabase
          .from('documents')
          .select('*')
          .eq('id', documentId)
          .single();

        if (error) throw error;
        setDocument(data);
      } catch (err) {
        console.error('Error fetching document:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [documentId]);

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return <ImageIcon className="h-5 w-5" />;
    if (mimeType === 'application/pdf') return <FileText className="h-5 w-5" />;
    return <File className="h-5 w-5" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleDownload = async () => {
    if (!document) return;
    
    try {
      setDownloading(true);
      const url = await getDownloadUrl(document);
      if (url) {
        window.open(url, '_blank');
      }
    } catch (err) {
      console.error('Error downloading document:', err);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!document) {
    return (
      <Card>
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground">Document not found</p>
        </CardContent>
      </Card>
    );
  }

  if (compact) {
    return (
      <div className="flex items-center gap-3 p-3 border rounded-md bg-secondary/20">
        <div className="text-primary">
          {getFileIcon(document.content_type)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{document.original_filename}</p>
          <p className="text-xs text-muted-foreground">{formatFileSize(document.size_bytes)}</p>
        </div>
        {showDownloadButton && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDownload}
            disabled={downloading}
          >
            {downloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>
    );
  }

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-primary/10 text-primary">
            {getFileIcon(document.content_type)}
          </div>
          <div className="flex-1 min-w-0">
            {specificationName && (
              <h4 className="font-semibold mb-1">{specificationName}</h4>
            )}
            <p className="text-sm font-medium mb-1 truncate">{document.original_filename}</p>
            
            <div className="flex flex-wrap gap-2 mb-3">
              {subcategoryName && (
                <Badge variant="outline" className="text-xs">
                  {subcategoryName}
                </Badge>
              )}
              {productCategoryName && (
                <Badge variant="secondary" className="text-xs">
                  {productCategoryName}
                </Badge>
              )}
            </div>

            {showMetadata && (
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <div>
                  <span className="font-medium">Size:</span> {formatFileSize(document.size_bytes)}
                </div>
                <div>
                  <span className="font-medium">Uploaded:</span> {format(new Date(document.created_at), 'MMM d, yyyy')}
                </div>
              </div>
            )}
          </div>

          {showDownloadButton && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              disabled={downloading}
              className="gap-2"
            >
              {downloading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Download
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};